import type { ReactNode } from "react";
import { Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Download, Sync } from "@mui/icons-material";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import { exportTableToExcel, type Column } from "@/components/DataTable";
import { Indicadores, type Indicador } from "@/components/Indicadores";
import { rotuloCompetencia } from "@/api/escala.service";
import { semResultado } from "@/components/escala/relatorio";

export type VisaoRelatorio = "grade" | "lista";

// Relatórios com grade: a grade é a visão padrão, a lista é o detalhe que vai pro Excel.
export const visoesComGrade = (rotuloGrade: string) => [
  { valor: "grade" as const, rotulo: rotuloGrade },
  { valor: "lista" as const, rotulo: "Lista" },
];

export interface ExportacaoRelatorio<T> {
  colunas: Column<T>[];
  linhas: T[];
  // Prefixo do arquivo; a competência entra depois, quando houver.
  arquivo: string;
  aba: string;
}

interface RelatorioLayoutProps<T> {
  carregando: boolean;
  filtro: ReactNode;
  // Quatro em todo relatório, pra faixa ter sempre o mesmo desenho.
  indicadores: [Indicador, Indicador, Indicador, Indicador];
  exportacao: ExportacaoRelatorio<T>;
  // Ausente nos relatórios que são foto do cadastro, sem mês.
  competencia?: { ano: number; mes: number };
  visoes?: { valor: VisaoRelatorio; rotulo: string }[];
  visao?: VisaoRelatorio;
  onVisao?: (visao: VisaoRelatorio) => void;
  legenda?: ReactNode;
  children: ReactNode;
}

// Molde comum dos relatórios da Escala: barra, indicadores, conteúdo num quadro e legenda.
export function RelatorioLayout<T>({
  carregando,
  filtro,
  indicadores,
  exportacao,
  competencia,
  visoes,
  visao,
  onVisao,
  legenda,
  children,
}: RelatorioLayoutProps<T>) {
  if (carregando) return <Sync />;

  const vazio = exportacao.linhas.length === 0;
  const exportar = () => {
    const sufixo = competencia ? `-${rotuloCompetencia(competencia.ano, competencia.mes)}` : "";
    exportTableToExcel(exportacao.colunas, exportacao.linhas, `${exportacao.arquivo}${sufixo}`, exportacao.aba);
  };

  return (
    <>
      <CrudToolbar>
        {filtro}
        {/* mb igual ao do badge do FilterModal, pra os dois lados ficarem alinhados */}
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 2 }}>
          {visoes && (
            <ToggleButtonGroup size="small" exclusive value={visao} onChange={(_, nova: VisaoRelatorio | null) => nova && onVisao?.(nova)}>
              {visoes.map((v) => (
                <ToggleButton key={v.valor} value={v.valor}>
                  {v.rotulo}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <Button variant="outlined" startIcon={<Download />} onClick={exportar} disabled={vazio}>
            Exportar
          </Button>
        </Stack>
      </CrudToolbar>

      <Indicadores itens={indicadores} />

      {vazio ? (
        <Alert severity="info">{semResultado(competencia?.ano, competencia?.mes)}</Alert>
      ) : (
        <>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", overflow: "hidden" }}>
            {children}
          </Box>
          {legenda && <Box sx={{ mt: 1.5 }}>{legenda}</Box>}
        </>
      )}
    </>
  );
}
